import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Spinner,
  Badge,
  Alert,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  getScheduledFollowups,
  reprocessScheduledFollowup,
  deleteScheduledFollowup,
  deleteMultipleScheduledFollowups,
  getScheduledFollowupsStats,
} from "./services/AutomationServices";
import {
  IconRefresh,
  IconTrash,
  IconEye,
  IconRobot,
  IconArrowLeft,
  IconCalendar,
  IconFilter,
} from "@tabler/icons-react";
import styled from "styled-components";

// Importar contexto do AgentHub (opcional)
import { useAgentHubContext } from "../AgentHub/contexts/AgentHubContext";

// Importar CSS mobile
import "./ScheduledFollowupsList.mobile.css";

// Importar DatatablePaginated
import DatatablePaginated from "../../jsx/components/DatatablePaginated";

const ScheduledFollowupsList = () => {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // Estados para seleção múltipla
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteButton, setShowDeleteButton] = useState(false);

  // Estados para filtros
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDate, setFilterDate] = useState("all");

  const navigate = useNavigate();
  const { agentId: urlAgentId } = useParams();

  // Tentar obter contexto do AgentHub (opcional)
  const agentContext = useAgentHubContext();
  const agentId = agentContext?.agentId || urlAgentId;
  // Detectar se está no AgentHub pela URL ou contexto
  const isInsideAgentHub =
    Boolean(agentContext) || window.location.pathname.includes("/agent-hub/");

  const fetchFollowups = async (
    page = 1,
    pageSize = 10,
    statusFilter = filterStatus,
    dateFilter = filterDate
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        ...(agentId && { agent_id: agentId }),
      };

      // Adicionar filtro de data primeiro
      if (dateFilter && dateFilter !== "all") {
        const now = new Date();
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );

        switch (dateFilter) {
          case "today":
            params.analysis_date__date = today.toISOString().split("T")[0];
            break;
          case "next7days":
            const nextWeek = new Date(today);
            nextWeek.setDate(nextWeek.getDate() + 7);
            params.analysis_date__gte = today.toISOString().split("T")[0];
            params.analysis_date__lt = nextWeek.toISOString().split("T")[0];
            break;
          case "overdue":
            params.analysis_date__lt = today.toISOString().split("T")[0];
            // Para "overdue", só forçar status como pending se for o comportamento padrão
            // mas não quando o usuário explicitamente escolheu "Todos"
            break;
          default:
            break;
        }
      }

      // Adicionar filtro de status (depois do filtro de data para não ser sobrescrito)
      if (statusFilter && statusFilter !== "all") {
        params.status = statusFilter;
      }

      const [followupsData, statsData] = await Promise.all([
        getScheduledFollowups(params),
        getScheduledFollowupsStats(agentId ? { agent_id: agentId } : {}),
      ]);

      setFollowups(followupsData.results || []);
      setTotalRows(followupsData.count || 0);
      setStats(statsData || {});
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao buscar os follow-ups agendados.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowups(currentPage, perPage);
  }, [agentId]);

  // Reagir às mudanças de filtros
  useEffect(() => {
    setCurrentPage(1); // Reset para primeira página quando filtros mudam
    fetchFollowups(1, perPage, filterStatus, filterDate);
  }, [filterStatus, filterDate]);

  // Função para lidar com mudança de página
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchFollowups(page, perPage, filterStatus, filterDate);
  };

  // Configuração para filtros de busca
  const filterByField = {
    fields: ["contact_name", "phone"],
    labels: ["Nome do Contato", "Telefone"],
  };

  const handleReprocess = async (id, e) => {
    e.stopPropagation();
    try {
      await reprocessScheduledFollowup(id);

      // Atualizar o item na lista local
      setFollowups((prevFollowups) =>
        prevFollowups.map((followup) =>
          followup.id === id
            ? {
                ...followup,
                status: "pending",
                processed_at: null,
                error_message: null,
              }
            : followup
        )
      );

      Swal.fire({
        title: "Sucesso!",
        text: "Follow-up reagendado para reprocessamento.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao reprocessar o follow-up.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDelete = (id, contactName, e) => {
    e.stopPropagation();
    Swal.fire({
      title: "Tem certeza?",
      text: `Deseja realmente excluir o follow-up de "${contactName}"? Esta ação não pode ser desfeita.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteScheduledFollowup(id);

          // Atualizar lista removendo o item
          setFollowups((prevFollowups) =>
            prevFollowups.filter((followup) => followup.id !== id)
          );

          Swal.fire({
            title: "Excluído!",
            text: "Follow-up excluído com sucesso.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          Swal.fire({
            title: "Erro!",
            text: "Ocorreu um erro ao excluir o follow-up.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleShowDetails = (followup, e) => {
    e.stopPropagation();
    setSelectedFollowup(followup);
    setShowDetailModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "processed":
        return "success";
      case "failed":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Pendente";
      case "processed":
        return "Processado";
      case "failed":
        return "Falha";
      default:
        return status;
    }
  };

  const handleBackNavigation = () => {
    if (isInsideAgentHub) {
      navigate(`/agent-hub/${agentId}/automations`);
    } else {
      navigate("/automations");
    }
  };

  // Função para lidar com mudanças de seleção
  const handleSelectedRowsChange = (state) => {
    if (state.selectedRows && state.selectedRows.length > 0) {
      setSelectedRows(state.selectedRows);
      setShowDeleteButton(true);
    } else {
      setSelectedRows([]);
      setShowDeleteButton(false);
    }
  };

  // Função para exclusão em lote
  const handleBulkDelete = () => {
    const selectedIds = selectedRows.map((row) => row.id);
    const selectedNames = selectedRows.map(
      (row) => row.contact_name || row.phone
    );

    Swal.fire({
      title: "Tem certeza?",
      text: `Deseja realmente excluir ${selectedIds.length} follow-up(s) selecionado(s)? Esta ação não pode ser desfeita.`,
      html: `
        <div>
          <p>Deseja realmente excluir ${
            selectedIds.length
          } follow-up(s) selecionado(s)? Esta ação não pode ser desfeita.</p>
          <div style="max-height: 200px; overflow-y: auto; text-align: left; margin-top: 10px;">
            ${selectedNames.map((name) => `<div>• ${name}</div>`).join("")}
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteMultipleScheduledFollowups(selectedIds);

          // Atualizar lista removendo os items excluídos
          setFollowups((prevFollowups) =>
            prevFollowups.filter(
              (followup) => !selectedIds.includes(followup.id)
            )
          );

          // Resetar seleção
          setSelectedRows([]);
          setShowDeleteButton(false);

          // Recarregar stats
          fetchFollowups(currentPage, perPage, filterStatus, filterDate);

          Swal.fire({
            title: "Excluído!",
            text: `${selectedIds.length} follow-up(s) excluído(s) com sucesso.`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          Swal.fire({
            title: "Erro!",
            text: "Ocorreu um erro ao excluir os follow-ups.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  // Definir as colunas da tabela
  const columns = useMemo(
    () => [
      {
        name: "Contato",
        selector: (row) => row.contact_name || "Sem nome",
        sortable: true,
        cell: (row) => (
          <div>
            <div style={{ fontWeight: "600", color: "var(--text-primary)" }}>
              {row.contact_name || "Sem nome"}
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
              {row.phone}
            </div>
          </div>
        ),
        grow: 2,
      },
      {
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => (
          <Badge bg={getStatusColor(row.status)}>
            {getStatusText(row.status)}
          </Badge>
        ),
        width: "120px",
      },
      {
        name: "Automação",
        selector: (row) => row.automation_name || "N/A",
        sortable: true,
        grow: 1,
      },
      {
        name: "Agente",
        selector: (row) => row.agent_name || "N/A",
        sortable: true,
        width: "120px",
      },
      {
        name: "Agendado para",
        selector: (row) => row.analysis_date,
        sortable: true,
        cell: (row) => (
          <div style={{ fontSize: "12px" }}>
            {new Date(row.analysis_date).toLocaleString("pt-BR")}
          </div>
        ),
        width: "160px",
      },
      {
        name: "Processado em",
        selector: (row) => row.processed_at,
        sortable: true,
        cell: (row) => (
          <div style={{ fontSize: "12px" }}>
            {row.processed_at
              ? new Date(row.processed_at).toLocaleString("pt-BR")
              : "-"}
          </div>
        ),
        width: "160px",
      },
      {
        name: "Ações",
        cell: (row) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Ver detalhes</Tooltip>}
            >
              <Button
                size="sm"
                variant="primary"
                onClick={(e) => handleShowDetails(row, e)}
                style={{ padding: "4px 8px" }}
              >
                <IconEye size={14} />
              </Button>
            </OverlayTrigger>

            {/*  {row.status === "failed" && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Reprocessar</Tooltip>}
              >
                <Button
                  size="sm"
                  variant="info"
                  onClick={(e) => handleReprocess(row.id, e)}
                  style={{ padding: "4px 8px" }}
                >
                  <IconRefresh size={14} />
                </Button>
              </OverlayTrigger>
            )} */}

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Excluir</Tooltip>}
            >
              <Button
                size="sm"
                variant="danger"
                onClick={(e) =>
                  handleDelete(row.id, row.contact_name || row.phone, e)
                }
                style={{ padding: "4px 8px" }}
              >
                <IconTrash size={14} />
              </Button>
            </OverlayTrigger>
          </div>
        ),
        width: "150px",
        ignoreRowClick: true,
      },
    ],
    []
  );

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando follow-ups agendados...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderContent>
          <BackButton onClick={handleBackNavigation}>
            <IconArrowLeft size={20} />
          </BackButton>
          <HeaderInfo>
            <Title>Follow-ups Agendados</Title>
            <Subtitle>Gerencie análises automáticas de conversas</Subtitle>
          </HeaderInfo>
          {/*  <RefreshButton
            onClick={() =>
              fetchFollowups(currentPage, perPage, filterStatus, filterDate)
            }
          >
            <IconRefresh size={20} />
          </RefreshButton> */}
        </HeaderContent>
      </Header>

      {/* Stats */}
      <StatsContainer className="sfl-stats-mobile">
        <StatCard className="stat-card">
          <StatNumber className="stat-number">{stats.total || 0}</StatNumber>
          <StatLabel className="stat-label">Total</StatLabel>
        </StatCard>
        <StatCard className="stat-card">
          <StatNumber className="stat-number">{stats.pending || 0}</StatNumber>
          <StatLabel className="stat-label">Pendentes</StatLabel>
        </StatCard>
        <StatCard className="stat-card">
          <StatNumber className="stat-number">
            {stats.processed || 0}
          </StatNumber>
          <StatLabel className="stat-label">Processados</StatLabel>
        </StatCard>
        <StatCard className="stat-card">
          <StatNumber className="stat-number">{stats.failed || 0}</StatNumber>
          <StatLabel className="stat-label">Falhas</StatLabel>
        </StatCard>
        <StatCard className="stat-card">
          <StatNumber className="stat-number">
            {stats.today_followups || 0}
          </StatNumber>
          <StatLabel className="stat-label">Hoje</StatLabel>
        </StatCard>
        <StatCard className="stat-card">
          <StatNumber className="stat-number">
            {Math.round(stats.success_rate || 0)}%
          </StatNumber>
          <StatLabel className="stat-label">Taxa de Sucesso</StatLabel>
        </StatCard>
      </StatsContainer>

      {/* Filtros */}
      <FiltersContainer className="sfl-filters-mobile">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <h6
            style={{
              margin: 0,
              color: "var(--text-primary)",
              fontWeight: "600",
            }}
          >
            Filtros
          </h6>
          {(filterStatus !== "all" || filterDate !== "all") && (
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setFilterStatus("all");
                setFilterDate("all");
              }}
              style={{ padding: 0, fontSize: "12px", textDecoration: "none" }}
            >
              Limpar filtros
            </Button>
          )}
        </div>

        <FilterGroup className="filter-group">
          <FilterButton
            className="filter-button"
            active={filterStatus === "all"}
            onClick={() => setFilterStatus("all")}
          >
            <IconFilter size={16} />
            Todos
          </FilterButton>
          <FilterButton
            className="filter-button"
            active={filterStatus === "pending"}
            onClick={() => setFilterStatus("pending")}
          >
            Pendentes
          </FilterButton>
          <FilterButton
            className="filter-button"
            active={filterStatus === "processed"}
            onClick={() => setFilterStatus("processed")}
          >
            Processados
          </FilterButton>
          <FilterButton
            className="filter-button"
            active={filterStatus === "failed"}
            onClick={() => setFilterStatus("failed")}
          >
            Falhas
          </FilterButton>
        </FilterGroup>

        <FilterGroup className="filter-group">
          <FilterButton
            className="filter-button"
            active={filterDate === "all"}
            onClick={() => setFilterDate("all")}
          >
            <IconCalendar size={16} />
            Todas as datas
          </FilterButton>
          <FilterButton
            className="filter-button"
            active={filterDate === "today"}
            onClick={() => setFilterDate("today")}
          >
            Hoje
          </FilterButton>
          <FilterButton
            className="filter-button"
            active={filterDate === "next7days"}
            onClick={() => setFilterDate("next7days")}
          >
            Próximos 7 dias
          </FilterButton>
          <FilterButton
            className="filter-button"
            active={filterDate === "overdue"}
            onClick={() => setFilterDate("overdue")}
          >
            Atrasados
          </FilterButton>
        </FilterGroup>
      </FiltersContainer>

      {/* Botão de exclusão em lote - Desktop */}
      {showDeleteButton && (
        <div style={{ marginBottom: "20px" }} className="sfl-datatable-wrapper">
          <Button
            variant="danger"
            onClick={handleBulkDelete}
            style={{
              backgroundColor: "#dc3545",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              fontWeight: "600",
            }}
          >
            <IconTrash size={16} style={{ marginRight: "8px" }} />
            DELETAR SELECIONADOS ({selectedRows.length})
          </Button>
        </div>
      )}

      {/* Botão de exclusão em lote - Mobile */}
      {showDeleteButton && (
        <Button
          variant="danger"
          onClick={handleBulkDelete}
          className="sfl-bulk-delete-mobile sfl-mobile-cards"
          style={{
            backgroundColor: "#dc3545",
            border: "none",
            borderRadius: "8px",
            padding: "12px 20px",
            fontWeight: "600",
            width: "100%",
          }}
        >
          <IconTrash size={16} style={{ marginRight: "8px" }} />
          DELETAR SELECIONADOS ({selectedRows.length})
        </Button>
      )}

      {/* DataTable wrapper responsivo */}
      <div className="sfl-mobile-container">
        <div
          className="sfl-datatable-wrapper"
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <DatatablePaginated
            data={followups}
            columns={columns}
            title="Follow-ups Agendados"
            totalRows={totalRows}
            isLoading={loading}
            handlePageChange={handlePageChange}
            filterByField={filterByField}
            filterDefaultField="contact_name"
            searchBarType={1}
            selectable={true}
            onSelectedRowsChange={handleSelectedRowsChange}
          />
        </div>

        {/* Mobile Cards */}
        <div className="sfl-mobile-cards">
          {loading ? (
            <div className="sfl-mobile-loader">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Carregando...</p>
            </div>
          ) : followups.length === 0 ? (
            <div className="sfl-mobile-empty">
              <IconRobot size={48} />
              <h3>Nenhum follow-up encontrado</h3>
              <p>
                Nenhum follow-up agendado foi encontrado com os filtros
                selecionados.
              </p>
            </div>
          ) : (
            <>
              {followups.map((followup) => (
                <div
                  key={followup.id}
                  className={`sfl-card ${
                    selectedRows.some((row) => row.id === followup.id)
                      ? "sfl-selected sfl-has-checkbox"
                      : ""
                  }`}
                >
                  {/* Checkbox para seleção múltipla */}
                  <div className="sfl-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRows.some(
                        (row) => row.id === followup.id
                      )}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const newSelectedRows = [...selectedRows, followup];
                          setSelectedRows(newSelectedRows);
                          setShowDeleteButton(newSelectedRows.length > 0);
                        } else {
                          const newSelectedRows = selectedRows.filter(
                            (row) => row.id !== followup.id
                          );
                          setSelectedRows(newSelectedRows);
                          setShowDeleteButton(newSelectedRows.length > 0);
                        }
                      }}
                    />
                  </div>

                  {/* Header do card */}
                  <div className="sfl-card-header">
                    <div className="sfl-contact-info">
                      <h4 className="sfl-contact-name">
                        {followup.contact_name || "Sem nome"}
                      </h4>
                      <p className="sfl-contact-phone">{followup.phone}</p>
                    </div>
                    <Badge
                      bg={getStatusColor(followup.status)}
                      className="sfl-status-badge"
                    >
                      {getStatusText(followup.status)}
                    </Badge>
                  </div>

                  {/* Body do card */}
                  <div className="sfl-card-body">
                    <div className="sfl-info-row">
                      <span className="sfl-info-label">Automação:</span>
                      <span className="sfl-info-value">
                        {followup.automation_name || "N/A"}
                      </span>
                    </div>
                    <div className="sfl-info-row">
                      <span className="sfl-info-label">Agente:</span>
                      <span className="sfl-info-value">
                        {followup.agent_name || "N/A"}
                      </span>
                    </div>
                    <div className="sfl-info-row">
                      <span className="sfl-info-label">Agendado para:</span>
                      <span className="sfl-info-value sfl-date-value">
                        {new Date(followup.analysis_date).toLocaleString(
                          "pt-BR"
                        )}
                      </span>
                    </div>
                    {followup.processed_at && (
                      <div className="sfl-info-row">
                        <span className="sfl-info-label">Processado em:</span>
                        <span className="sfl-info-value sfl-date-value">
                          {new Date(followup.processed_at).toLocaleString(
                            "pt-BR"
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions do card */}
                  <div className="sfl-card-actions">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={(e) => handleShowDetails(followup, e)}
                      className="sfl-action-btn"
                    >
                      <IconEye size={14} />
                      Ver
                    </Button>

                    {followup.status === "failed" && (
                      <Button
                        size="sm"
                        variant="info"
                        onClick={(e) => handleReprocess(followup.id, e)}
                        className="sfl-action-btn"
                      >
                        <IconRefresh size={14} />
                        Reprocessar
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="danger"
                      onClick={(e) =>
                        handleDelete(
                          followup.id,
                          followup.contact_name || followup.phone,
                          e
                        )
                      }
                      className="sfl-action-btn"
                    >
                      <IconTrash size={14} />
                      Excluir
                    </Button>
                  </div>
                </div>
              ))}

              {/* Paginação mobile */}
              <div className="sfl-mobile-pagination">
                <div className="sfl-pagination-info">
                  Página {currentPage} de {Math.ceil(totalRows / perPage)}
                </div>
                <div className="sfl-pagination-buttons">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="sfl-pagination-btn"
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={currentPage >= Math.ceil(totalRows / perPage)}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="sfl-pagination-btn"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <IconRobot size={20} className="me-2" />
            Detalhes do Follow-up
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFollowup && (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Contato:</strong>{" "}
                  {selectedFollowup.contact_name || "Sem nome"}
                </div>
                <div className="col-md-6">
                  <strong>Telefone:</strong> {selectedFollowup.phone}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Automação:</strong>{" "}
                  {selectedFollowup.automation_name || "N/A"}
                </div>
                <div className="col-md-6">
                  <strong>Status:</strong>{" "}
                  <Badge bg={getStatusColor(selectedFollowup.status)}>
                    {getStatusText(selectedFollowup.status)}
                  </Badge>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Agendado para:</strong>{" "}
                  {new Date(selectedFollowup.analysis_date).toLocaleString(
                    "pt-BR"
                  )}
                </div>
                {selectedFollowup.processed_at && (
                  <div className="col-md-6">
                    <strong>Processado em:</strong>{" "}
                    {new Date(selectedFollowup.processed_at).toLocaleString(
                      "pt-BR"
                    )}
                  </div>
                )}
              </div>

              {selectedFollowup.analysis_result && (
                <div className="mb-3">
                  <h6>Resultado da Análise:</h6>
                  <Alert variant="info">
                    <pre style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                      {selectedFollowup.analysis_result}
                    </pre>
                  </Alert>
                </div>
              )}

              {selectedFollowup.error_message && (
                <div className="mb-3">
                  <h6>Erro:</h6>
                  <Alert variant="danger">
                    {selectedFollowup.error_message}
                  </Alert>
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <strong>Criado em:</strong>{" "}
                  {new Date(selectedFollowup.created_at).toLocaleString(
                    "pt-BR"
                  )}
                </div>
                <div className="col-md-6">
                  <strong>Atualizado em:</strong>{" "}
                  {new Date(selectedFollowup.updated_at).toLocaleString(
                    "pt-BR"
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedFollowup?.status === "failed" && (
            <Button
              variant="warning"
              onClick={() => {
                handleReprocess(selectedFollowup.id, {
                  stopPropagation: () => {},
                });
                setShowDetailModal(false);
              }}
            >
              <IconRefresh size={16} className="me-1" />
              Reprocessar
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: #6c757d;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: var(--surface);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
    color: var(--text-primary);
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--shadow-sm);
  text-align: center;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  text-transform: uppercase;
  font-weight: 500;
`;

// Styled components para filtros
const FiltersContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 16px;
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background: ${(props) => (props.active ? "var(--primary)" : "white")};
  color: ${(props) => (props.active ? "white" : "var(--text-secondary)")};
  border: 2px solid
    ${(props) => (props.active ? "var(--primary)" : "var(--border-color)")};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${(props) =>
      props.active ? "var(--primary-hover)" : "var(--surface)"};
    border-color: ${(props) =>
      props.active ? "var(--primary-hover)" : "var(--border-color-hover)"};
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 10px;
  }
`;

export default ScheduledFollowupsList;
